import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Accordion from '@/shared/components/Accordion/Accordion'
import { Theme } from '../../render/theme'
import FontPicker from './FontPicker'
import PageColors from './PageColors'
import TokenSelect from './TokenSelect'
import styles from './PropertiesPanel.module.scss'

const RADII: Theme['radius'][] = ['none', 'S', 'M', 'L']
const BUTTON_STYLES: Theme['buttonStyle'][] = ['solid', 'outline', 'pill']

interface PageSettingsProps {
  theme: Theme
  onChange: (theme: Theme) => void
  onPreview: (theme: Theme) => void
  onPreviewEnd: () => void
}

/**
 * Everything that belongs to the whole page rather than to what is selected.
 *
 * Collapsed groups, because these are set once and then left alone, and in the
 * panel the header's cog opens, because that is where this app keeps a page's
 * settings (menu management, the floor planner). They used to appear only when
 * nothing at all was selected, which meant the colours were unreachable unless
 * you happened to click the empty part of the canvas first.
 */
function PageSettings(props: PageSettingsProps) {
  const { theme, onChange, onPreview, onPreviewEnd } = props
  const { t } = useTranslation()
  const [openGroup, setOpenGroup] = useState(-1)

  const toggle = (index: number) =>
    setOpenGroup(openGroup === index ? -1 : index)

  return (
    <>
      <Accordion
        title={t('WEBSITE.COLORS.TITLE')}
        expanded={openGroup === 0}
        onToggle={() => toggle(0)}
      >
        <PageColors
          theme={theme}
          onChange={onChange}
          onPreview={onPreview}
          onPreviewEnd={onPreviewEnd}
        />
      </Accordion>

      <Accordion
        title={t('WEBSITE.PANEL.STYLE')}
        expanded={openGroup === 1}
        onToggle={() => toggle(1)}
      >
        <p className={`label-bold-medium ${styles.fieldLabel}`}>
          {t('WEBSITE.SETTINGS.FONTS')}
        </p>
        <FontPicker theme={theme} onChange={onChange} />
        <TokenSelect
          label={t('WEBSITE.PANEL.RADIUS')}
          value={theme.radius}
          options={RADII}
          translate={value => t(`WEBSITE.RADIUS.${value}`)}
          onChange={value =>
            onChange({ ...theme, radius: value as Theme['radius'] })
          }
        />
        <TokenSelect
          label={t('WEBSITE.PANEL.BUTTONS')}
          value={theme.buttonStyle}
          options={BUTTON_STYLES}
          translate={value => t(`WEBSITE.BUTTON_STYLE.${value}`)}
          onChange={value =>
            onChange({ ...theme, buttonStyle: value as Theme['buttonStyle'] })
          }
        />
      </Accordion>
    </>
  )
}

export default PageSettings
